import React, { FunctionComponent, useCallback } from "react";
import styles from "./Header1.module.scss";
import Header1Props from "./Header1Props.interface";

import LanguageSelector from "./components/LanguageSelector/LanguageSelector";
import LocationSelector from "./components/LocationSelector/LocationSelector";
import MenuOverlay from "./components/MenuOverlay/MenuOverlay";
import Navigation from "./components/Navigation/Navigation";
import setHeaderStyles from "./hooks/setHeaderStyles";
import SocialLinks from "./components/SocialLinks/SocialLinks";

import { CLOSE_ALL_OVERLAYS, OPEN_MENU } from "layout/redux/overlay.actions";
import { useDispatch, useSelector } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import ImageViewer from "components/ImageViewer/ImageViewer";
import useScrollState from "layout/Header/hooks/useScrollState";
import FixrOverlay from "../Header2/components/FixrOverlay/FixrOverlay";

const Header1: FunctionComponent<Header1Props> = (props) => {
    const dispatch = useDispatch();

    let header: any;
    if (props?.theme?.header?.[0] && props?.theme?.header.length > 0) {
        header = props?.theme?.header[0]?.content;
    }

    const headerStyles = setHeaderStyles(header);
    const scrollTypeClass = useScrollState(header?.scrollType, styles);

    const settings = useSelector((state) => state.settings.content);
    const page = useSelector((state) => state.page.content);

    const overlay = useSelector((state) => state.overlay);
    const overlayActiveClass = overlay.currentOverlay === "MENU" ? styles.is_active : undefined;

    const logoTypeClass = header?.logoType ? styles[`logo_type_${header.logoType}`] : undefined;
    const logoImageType = header?.logoType === "Vertical" ? "containX" : "containY";
    const imageAspectRatio = getImageAspectRatio(undefined, header?.logo?.umbracoWidth, header?.logo?.umbracoHeight);

    const headerButtonContentType = header?.headerButtons?.[0] && header.headerButtons.length > 0 ? header.headerButtons[0].content._contentType : undefined;

    const toggleMenu = useCallback(
        (event: any) => {
            if (event) {
                event.stopPropagation();
            }

            if (overlay?.currentOverlay === "MENU") {
                dispatch({
                    type: CLOSE_ALL_OVERLAYS,
                });
            } else {
                dispatch({
                    type: OPEN_MENU,
                });
            }
        },
        [overlay.currentOverlay]
    );

    return (
        <header
            className={combineClasses(styles.wrapper, scrollTypeClass)}
            style={headerStyles}
        >
            <div className={combineClasses(styles.inner, logoTypeClass)}>
                {header?.logo ? (
                    <Anchor
                        className={styles.image_link}
                        to={page?._homeNodeUrl}
                    >
                        <ImageViewer
                            className={styles.image}
                            src={header.logo.umbracoFile.src}
                            extensionType="png"
                            type={logoImageType}
                            aspectRatioX={imageAspectRatio.aspectRatioX}
                            aspectRatioY={imageAspectRatio.aspectRatioY}
                        />
                    </Anchor>
                ) : null}

                <button
                    className={combineClasses(styles.menu_toggle_button, overlayActiveClass)}
                    onClick={(event) => toggleMenu(event)}
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>

                <div className={styles.content}>
                    {/* Toolbar */}
                    <div className={styles.toolbar}>
                        {/* Social Links */}
                        <SocialLinks {...props} />

                        {/* Language Selector */}
                        {page?._cultures && Object.keys(page._cultures).length > 1 ? <LanguageSelector /> : null}

                        {/* Location Selector */}
                        {settings?.locations && settings.locations.length > 0 ? <LocationSelector locations={settings.locations} /> : null}
                    </div>

                    {/* Content */}
                    <div className={styles.content_inner}>
                        <Navigation menu={props.menu} />

                        {/* Buttons */}
                        {props.headerButtons?.map((button, index) => {
                            return (
                                <Button
                                    key={index}
                                    to={button.content.link}
                                    className={styles.action_button}
                                    contentType={headerButtonContentType}
                                    {...button.content}
                                >
                                    {button.content.label}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {overlay.currentOverlay === "MENU" ? (
                <MenuOverlay
                    header={header}
                    {...props}
                />
            ) : null}

            {overlay.currentOverlay === "FIXR_OVERLAY" ? (
                <FixrOverlay
                    {...props}
                    state={overlay.currentOverlay}
                />
            ) : null}
        </header>
    );
};

export default Header1;
